import { api } from '../api';

export type LLMSettingsSource = 'stored' | 'environment' | 'missing';

export interface LLMConfig {
  provider: string;
  model: string;
  temperature: number;
  max_output_tokens: number;
  reasoning_effort: 'minimal' | 'low' | 'medium' | 'high';
  text_verbosity: 'low' | 'medium' | 'high';
  base_url?: string | null;
  cache_ttl_seconds: number;
  cache_max_size: number;
  max_retries: number;
  request_timeout: number;
}

export interface LLMSettingsResponse {
  has_api_key: boolean;
  source: LLMSettingsSource;
  updated_at?: string | null;
  settings: LLMConfig;
}

export interface PolicyDocumentResponse {
  policy_text: string;
  source: 'workspace' | 'default';
  workspace_path: string;
}

export interface ConnectorSummary {
  provider: string;
  connected: boolean;
  updated_at?: string | null;
  metadata: Record<string, unknown>;
}

export interface ConnectorListResponse {
  connectors: ConnectorSummary[];
}

export type WorkspaceRole = 'viewer' | 'editor' | 'approver' | 'admin';

export interface IntegrationSettingsEntry {
  integration: string;
  config: Record<string, unknown>;
  secret_fields: Record<string, boolean>;
  updated_at?: string | null;
}

export interface IntegrationSettingsResponse {
  integrations: IntegrationSettingsEntry[];
}

export interface ApprovalRoleMappingsResponse {
  mappings: Record<string, WorkspaceRole>;
}

export interface UsageBreakdown {
  prompt_tokens: number;
  completion_tokens: number;
  total_tokens: number;
}

export interface UsageSummaryResponse {
  totals: UsageBreakdown;
  records: number;
  last_updated?: string | null;
}

export interface UsageTimelinePoint {
  date: string;
  totals: UsageBreakdown;
  records: number;
}

export interface UsageTimelineResponse {
  points: UsageTimelinePoint[];
}

export interface DailyUsageEvent {
  timestamp: string;
  prompt_tokens: number;
  completion_tokens: number;
  total_tokens: number;
  model?: string | null;
  source?: string | null;
  tool_names: string[];
}

export interface DailyUsageResponse {
  date: string;
  events: DailyUsageEvent[];
}

export interface ActionLogEntry {
  id: string;
  actor: string;
  action_type: string;
  status: string;
  timestamp: string;
  usage: Record<string, unknown>;
}

export interface ActionLogResponse {
  entries: ActionLogEntry[];
}

export const fetchLLMSettings = async (): Promise<LLMSettingsResponse> => {
  return api.get('api/settings/llm').json<LLMSettingsResponse>();
};

export const updateLLMApiKey = async (apiKey: string): Promise<LLMSettingsResponse> => {
  return api
    .post('api/settings/llm/key', { json: { api_key: apiKey } })
    .json<LLMSettingsResponse>();
};

export const deleteLLMApiKey = async (): Promise<LLMSettingsResponse> => {
  return api.delete('api/settings/llm/key').json<LLMSettingsResponse>();
};

export const fetchPolicyDocument = async (): Promise<PolicyDocumentResponse> => {
  return api.get('api/settings/policy').json<PolicyDocumentResponse>();
};

export const updatePolicyDocument = async (policyText: string): Promise<PolicyDocumentResponse> => {
  return api
    .put('api/settings/policy', { json: { policy_text: policyText } })
    .json<PolicyDocumentResponse>();
};

export const fetchConnectorSummaries = async (): Promise<ConnectorSummary[]> => {
  const response = await api.get('api/settings/connectors').json<ConnectorListResponse>();
  return response.connectors;
};

export const fetchIntegrationSettings = async (): Promise<IntegrationSettingsEntry[]> => {
  const response = await api.get('api/settings/integrations').json<IntegrationSettingsResponse>();
  return response.integrations;
};

export const updateIntegrationSettings = async ({
  integration,
  config,
}: {
  integration: string;
  config: Record<string, unknown>;
}): Promise<IntegrationSettingsEntry> => {
  const response = await api
    .put(`api/settings/integrations/${integration}`, { json: { config } })
    .json<IntegrationSettingsResponse>();
  return response.integrations[0];
};

export const deleteIntegrationSettings = async (
  integration: string,
): Promise<IntegrationSettingsEntry[]> => {
  const response = await api
    .delete(`api/settings/integrations/${integration}`)
    .json<IntegrationSettingsResponse>();
  return response.integrations;
};

export const configureConnector = async ({
  provider,
  metadata,
}: {
  provider: string;
  metadata: Record<string, unknown>;
}): Promise<ConnectorSummary> => {
  const response = await api
    .put(`api/settings/connectors/${provider}`, { json: { metadata } })
    .json<ConnectorListResponse>();
  return response.connectors[0];
};

export const disconnectConnector = async (provider: string): Promise<ConnectorSummary> => {
  const response = await api
    .delete(`api/settings/connectors/${provider}`)
    .json<ConnectorListResponse>();
  return response.connectors[0];
};

export const fetchApprovalRoleMappings = async (): Promise<Record<string, WorkspaceRole>> => {
  const response = await api
    .get('api/settings/approvals/roles')
    .json<ApprovalRoleMappingsResponse>();
  return response.mappings;
};

export const updateApprovalRoleMappings = async (
  mappings: Record<string, WorkspaceRole>,
): Promise<Record<string, WorkspaceRole>> => {
  const response = await api
    .put('api/settings/approvals/roles', { json: { mappings } })
    .json<ApprovalRoleMappingsResponse>();
  return response.mappings;
};

export const fetchUsageSummary = async (): Promise<UsageSummaryResponse> => {
  return api.get('api/settings/usage').json<UsageSummaryResponse>();
};

export const fetchUsageTimeline = async (): Promise<UsageTimelineResponse> => {
  return api.get('api/settings/usage/timeline').json<UsageTimelineResponse>();
};

export const fetchDailyUsage = async (date: string): Promise<DailyUsageResponse> => {
  return api
    .get('api/settings/usage/day', { searchParams: { date } })
    .json<DailyUsageResponse>();
};

export const fetchActionLogs = async (limit = 25): Promise<ActionLogEntry[]> => {
  const response = await api
    .get('api/settings/logs', { searchParams: { limit: String(limit) } })
    .json<ActionLogResponse>();
  return response.entries;
};

// NOTE: Raw Prometheus text from /metrics is no longer surfaced in the Settings UI.
